import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, FastForward, Rewind, Music } from 'lucide-react';

interface AudioPlayerProps {
  audioUrl: string;
  word: string;
  show?: boolean;
  onEnded?: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, word, show = true, onEnded }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress);
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    if (onEnded) onEnded();
  };

  const handleSeek = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (audioRef.current) {
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const width = progressBar.offsetWidth;
      const newTime = (x / width) * duration;
      audioRef.current.currentTime = newTime;
    }
  };

  const handlePlaybackRateChange = (rate: number) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };

  useEffect(() => {
    // Reset progress when audioUrl changes
    setProgress(0);
    setCurrentTime(0);
    setIsPlaying(false);
  }, [audioUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return isNaN(minutes) || isNaN(parseInt(seconds)) ? '0:00' : `${minutes}:${seconds}`;
  };

  return (
    <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg p-4 w-full max-w-md shadow-lg">
      <div className="flex items-center space-x-4">
        <div className='bg-white/20 rounded-full p-2'>
            <Music size={24} />
        </div>
        <div className="flex-1">
          <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1'>
            <h3 className="font-bold text-lg">{word}</h3>
            <span className="text-xs font-mono">{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>

          <div
            className="relative h-2 rounded-full bg-white/30 mt-2 cursor-pointer"
            onClick={handleSeek}
          >
            <div
              className="absolute top-0 left-0 h-full bg-white rounded-full transition-all duration-150"
              style={{ width: `${progress}%` }}
            >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 bg-white rounded-full shadow-md"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center space-x-6 mt-4">
        <button onClick={() => audioRef.current && (audioRef.current.currentTime -= 5)} className="text-white/80 hover:text-white transition-colors">
          <Rewind size={22} />
        </button>
        <button
          onClick={togglePlay}
          className="h-14 w-14 rounded-full bg-white text-indigo-600 flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
        </button>
        <button onClick={() => audioRef.current && (audioRef.current.currentTime += 5)} className="text-white/80 hover:text-white transition-colors">
          <FastForward size={22} />
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
        {[0.8, 0.9, 1, 1.2, 1.5].map(rate => (
            <button
                key={rate}
                onClick={() => handlePlaybackRateChange(rate)}
                className={`text-[11px] sm:text-xs px-2 py-1 sm:px-3 rounded-full transition-colors ${playbackRate === rate ? 'bg-white text-indigo-600 font-bold' : 'bg-white/20 text-white/80 hover:bg-white/30'}`}
            >
                {rate}x
            </button>
        ))}
      </div>
      
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        className="hidden"
      />
    </div>
  );
};

export default AudioPlayer;